/**
 * Animate one of several whimsical SVG paths to the left and right of an object.
 *
 * For a component that does so on hover, see HoverDecorationWrapper.
 */

import {css, StyleSheet} from "aphrodite";
import * as React from "react";

import {AnimatedLineDrawing, AnimationGroup} from "@khanacademy/khannotations";
import {
    withActionScheduler,
    type ITimeout,
    type WithActionSchedulerProps,
    type WithoutActionScheduler,
} from "@khanacademy/wonder-blocks-timing";

import {MarketingColors} from "./constants.ts";

type Decoration = {
    left: string;
    right: string;
};

const DECORATION_PATHS: ReadonlyArray<Decoration> = [
    {
        left: "M-11.45 11.43C-15.39 9.79 -20.72 6.21 -25 6.21M-10.4 24.43C-14.16 24.43 -21.76 23.02 -25 24.98M-7.28 41.66C-10.33 44.8 -13.52 48.1 -17.7 50",
        right: "M6.26 5.17C10.57 4.02 14.65 2.74 18.77 1M3.13 23.94C8.47 24.04 13.52 25.25 18.77 26.02M0 40.62C4.88 42.36 9.64 44.26 14.6 45.83",
    },
    {
        left: "M-31.4 20.51C-25.99 18.91 -20.46 18.77 -14.89 18.07C-13.64 17.91 -12.44 17.9 -11.34 17.24C-10.81 16.92 -24.21 4.52 -25.6 2.61M-57.6 1.37C-42.5 5.42 -26.72 9.91 -12.87 17.14M-49.32 44.71C-41.53 44.84 -33.83 46.98 -26.8 50.09C-24.43 51.14 -20.49 51.56 -18.57 53.35C-17.71 54.15 -32.19 63.97 -33.44 64.87C-38.74 68.71 -44.42 72.21 -49.85 75.7M-111.86 74.37C-102.95 72.36 -94.39 69.5 -85.49 67.4C-64.15 62.37 -42.52 58.07 -21.02 53.49",
        right: "M12.676 46.7859C31.1659 38.9482 50.6575 33.5267 69.8122 27.5658C77.4664 25.1839 85.3982 23.264 92.8291 20.2415C95.7993 19.0333 98.8613 18.0829 101.823 16.8421C103.629 16.086 104.176 16.3107 101.933 16.0717 M34.413 24.0424C27.8443 31.3365 20.2736 37.376 13.0573 44.0409C9.28506 47.525 9.15189 47.3367 13.6326 48.4658C23.3299 50.9095 53.1192 54.3662 43.1033 54.1952 M24.0311 3.78092C27.245 -0.483128 24.685 2.94157 23.1752 5.09983C19.2961 10.6448 14.4175 15.3531 9.77586 20.2343C7.96138 22.1424 5.51749 25.2897 3.14135 26.4275 M4.39502 14.5423C4.34896 17.2393 4.08309 19.5468 3.26519 22.1353C3.04826 22.8218 1.21132 26.6607 1.78637 27.3885C2.38968 28.1522 5.42877 26.6416 5.91396 26.4589C9.45673 25.1247 13.083 24.1872 16.748 23.2546 M14.8048 89.8033C13.1843 88.1264 1.91507 75.6612 6.42796 74.8385C9.32958 74.3095 26.145 71.7487 27.5241 74.6287 M9.56087 77.62C-7.08089 70.2758 43.6404 90.5884 59.769 99",
    },
    {
        left: "M-46.38 2.24C-44.78 4.82 -41.8 7.07 -40.5 9.67M-47 9.05C-44.84 6.35 -42.71 3.65 -40.5 1M-29 41C-29 53 -29 65 -29 77M-40 62C-30.48 62 -21.39 61.56 -12 60",
        right: "M17.01 1C17.1 9.26 18.51 17.53 18.51 25.72M7.27 13.73C13.57 13.73 19.75 13.77 26 12.99M47.45 36C45.5 49.65 47.45 64.34 47.45 78.14C47.45 86.76 47.45 95.38 47.45 104M24 68.83C39.79 71.21 56.25 70.27 72.07 68.83M37.09 56.93C29.85 47.59 40.23 60.52 41.84 62.99C48.22 72.74 54.15 82.86 59.36 93.28M31.03 87.59C44.02 74.02 55.3 58.59 67.38 44.21M9.5 55.5C6.44 54.33 1 53.36 1 58.25C1 60.17 2.22 62.87 4.5 63C6.59 63.12 8.94 62.66 9.47 60.25C10.29 56.58 8.28 56.37 6.5 54",
    },
    {
        left: "M-45.77 23C-44.76 25.71 -45.47 28.29 -48.07 29.77C-49.21 30.41 -50.2 31.33 -51.36 31.96C-52.46 32.55 -52.02 32.37 -50.96 32.37C-48.91 32.37 -46.68 33.27 -45.19 34.78C-44.63 35.35 -44.24 36.35 -43.87 37.05C-43.17 38.4 -43.39 38.23 -42.98 37.05C-42.32 35.17 -41.05 32.62 -39.67 31.21C-39.14 30.68 -38.36 30.07 -37.7 29.69C-36.52 29.01 -37.09 28.6 -37.55 29.19C-38.49 30.38 -40.94 28.12 -41.68 27.68C-42.57 27.17 -42.94 26.18 -43.62 25.49C-44.27 24.83 -44.92 24.14 -45.44 23.33M-17.43 1C-16.69 2.99 -17.21 4.88 -19.12 5.96C-19.95 6.43 -20.68 7.11 -21.53 7.57C-22.34 8 -22.01 7.87 -21.24 7.87C-19.74 7.87 -18.1 8.53 -17 9.64C-16.6 10.05 -16.31 10.79 -16.04 11.3C-15.52 12.3 -15.69 12.17 -15.38 11.3C-14.9 9.92 -13.97 8.06 -12.96 7.02C-12.57 6.63 -12 6.19 -11.51 5.91C-10.65 5.41 -11.06 5.11 -11.4 5.54C-12.09 6.41 -13.89 4.75 -14.43 4.43C-15.08 4.06 -15.36 3.33 -15.85 2.83C-16.33 2.34 -16.81 1.83 -17.19 1.25",
        right: "M29.62 1C30.26 4.84 27.73 9.55 24.29 11.45C22.92 12.22 21.71 13.19 20.25 13.84C17.99 14.84 19.17 14.94 20.8 14.94C22.49 14.94 23.92 14.84 25.58 14.94C27.32 15.03 27.84 16.81 29.05 17.5C29.81 17.94 30.45 20.17 30.91 20.99C31.31 21.72 32.19 25.36 32.19 23.45C32.19 19.96 34.56 15.32 37.89 13.84C39.08 13.31 40.41 12.36 41.58 12.02C43.12 11.58 39.97 12.27 39.17 11.92C37.96 11.38 36.3 11.79 35.21 10.82C34.41 10.1 32.12 9.39 31.85 8.15C31.6 7.03 30.66 6.01 30.34 4.87C30.05 3.86 29.74 2.69 29.25 1.73M6.81 28C7.76 30.53 7.1 32.94 4.67 34.31C3.6 34.91 2.68 35.78 1.6 36.36C0.57 36.92 0.98 36.74 1.97 36.74C3.88 36.74 5.97 37.58 7.36 39C7.88 39.52 8.25 40.46 8.59 41.11C9.24 42.38 9.04 42.21 9.42 41.11C10.04 39.36 11.22 36.98 12.51 35.67C13 35.17 13.73 34.6 14.35 34.24C15.45 33.61 14.92 33.22 14.49 33.78C13.61 34.89 11.33 32.77 10.63 32.37C9.81 31.89 9.46 30.97 8.83 30.32C8.22 29.71 7.61 29.06 7.12 28.31M64.32 57C64.86 58.27 64.48 59.47 63.09 60.16C62.49 60.46 61.96 60.89 61.34 61.18C60.75 61.46 60.99 61.37 61.56 61.37C62.65 61.37 63.84 61.79 64.63 62.5C64.93 62.76 65.14 63.23 65.34 63.56C65.71 64.19 65.59 64.11 65.81 63.56C66.16 62.68 66.84 61.49 67.58 60.83C67.86 60.58 68.28 60.3 68.63 60.12C69.25 59.8 68.95 59.61 68.71 59.89C68.21 60.44 66.9 59.39 66.5 59.19C66.03 58.95 65.83 58.48 65.47 58.16C65.12 57.85 64.78 57.53 64.5 57.16",
    },
    {
        left: "M-6.51 24.64C-19.24 17.86 -25.59 5.9 -31.77 -6.37C-33.39 -9.57 -42.17 -29.99 -47.97 -22.78C-55.22 -13.77 -52.19 -3.65 -48.09 5.99C-47.25 7.94 -49.97 2.94 -50.32 2.46C-52.2 -0.16 -54.97 -2.07 -57.66 -3.8C-60.99 -5.92 -64.69 -7.92 -67.03 -3.69C-69.53 0.85 -64.29 7.69 -62 11.05C-59.34 14.95 -56.01 17.65 -52.99 21.18C-51.69 22.7 -49.58 23.3 -48.41 24.75C-47.02 26.47 -52.44 22.91 -54.57 22.31C-57.18 21.58 -59.75 21.04 -62.47 20.91C-64.94 20.78 -64.36 25.87 -63.85 27.01C-62.61 29.83 -58.31 32.25 -55.74 33.72C-50.58 36.65 -45.59 39.52 -39.7 40.81C-33.46 42.18 -27.08 44.38 -20.75 45.05C-13.66 45.8 -6.86 45.33 0.18 44.35M-67.85 40.07C-63.97 40.63 -60.74 42.02 -57.24 43.73M-72.2 41.3C-68.21 46.01 -64.64 48.61 -58.79 50.33M-37.79 -36.22C-35.4 -33.27 -33.26 -30.1 -30.22 -27.81M-31.71 -45.72C-29.3 -42.06 -26.77 -38.49 -24.41 -34.81",
        right: "M-6.29 26.22C5.74 19.31 11.48 7.63 17.06 -4.36C18.51 -7.49 26.34 -27.38 32.13 -20.63C39.38 -12.18 36.77 -2.36 33.11 7.03C32.37 8.94 34.84 4.04 35.15 3.57C36.88 0.99 39.49 -0.93 42.03 -2.66C45.16 -4.81 48.66 -6.85 51.03 -2.85C53.58 1.44 48.75 8.18 46.64 11.48C44.2 15.31 41.09 18 38.29 21.49C37.08 22.99 35.08 23.63 33.99 25.06C32.7 26.75 37.82 23.17 39.84 22.53C42.33 21.74 44.78 21.15 47.4 20.94C49.76 20.74 49.36 25.65 48.91 26.77C47.8 29.52 43.74 31.97 41.31 33.46C36.44 36.44 31.73 39.35 26.1 40.77C20.14 42.27 14.08 44.58 8.01 45.42C1.22 46.35 -5.34 46.1 -12.13 45.37M31.48 47.9C35.19 45.39 38.66 42.98 42.84 41.43M31.31 58.53C35.87 55.79 40.39 53.02 44.85 50.14M11.35 -21.17C14.33 -26 18.18 -28.4 23.07 -30.95M10.39 -34.1C13.48 -35.58 16.46 -36.71 19.67 -37.83",
    },
    {
        left: "M-2.64 28.9C0.92 23.48 -7.97 15.15 -12.27 12.14C-15.94 9.57 -20.22 8.82 -24.5 7.85C-27.26 7.22 -33.07 8.89 -34.62 11.61C-34.86 12.01 -37.05 14.45 -36.64 13.44C-35.71 11.2 -33.48 8.2 -32.49 5.98C-30.13 0.7 -29.96 -4.41 -27.28 -9.48C-26.21 -11.49 -24.8 -13.9 -23.43 -15.61C-21.79 -17.66 -16.38 -13.16 -14.61 -12.14C-13.41 -11.46 -12.05 -10.84 -11.17 -9.74C-10.6 -9.03 -5.46 -13.69 -5.04 -14.28C-3.81 -15.97 -2 -17.49 -2.67 -19.84C-3.33 -22.14 -7.87 -24.01 -9.84 -24.67C-15.45 -26.54 -21.2 -28.67 -27.16 -28.67C-29.09 -28.67 -29.39 -27.42 -30.36 -25.88C-32.63 -22.28 -34.43 -18.39 -36.76 -14.81C-40.18 -9.52 -45.28 -6.81 -48.48 -0.95C-50.08 1.98 -51.68 7.14 -51.68 10.78C-52.21 14.51 -51.03 16.76 -50.61 17.71C-50.61 18.89 -50.67 19.83 -50.61 20.9C-50.47 23.48 -46.22 25.07 -45.17 27.3C-41.28 35.5 -33.75 43.4 -25.03 46.25C-20.85 47.62 -15.78 46.17 -11.7 44.89C-10.9 44.64 -4.5 42.36 -5.84 41.69M-11.17 28.37C-7.25 28.57 -11.6 28.99 -13.12 28.72C-17.15 28.01 -19.88 25.61 -23.1 23.33C-25.14 21.9 -28.17 19.96 -29.29 17.71M-12.77 -10.68C-14.45 -9.48 -17.59 -7.08 -19.49 -9.37C-21.11 -11.33 -19.74 -13.26 -18.55 -14.81M-7.44 -18.01C-9.12 -15.23 -10.75 -12.54 -12.77 -10.01M-12.77 -20.14C-13.8 -17.67 -14.96 -15.37 -16.5 -13.21M-18.1 -21.2C-19.3 -19.52 -20.39 -17.7 -21.3 -15.87M-27.16 -12.68C-30.12 -14.8 -33.08 -16.74 -36.22 -18.54M-27.16 -7.88C-30.28 -10.31 -33.64 -12.38 -36.76 -14.81M-50.08 -17.47C-53.11 -21.71 -56.21 -25.89 -59.41 -30M-58.08 -4.95C-60.72 -7.51 -63.87 -9.5 -66.87 -11.61M-64.47 18.24C-68.45 20.12 -72.68 20.79 -77 21.44M-40.49 53.15C-43.05 54.7 -44.97 56.48 -46.88 58.74",
        right: "M-9.18 28.9C-12.74 23.48 -3.85 15.15 0.45 12.14C4.12 9.57 8.4 8.82 12.68 7.85C15.44 7.22 21.25 8.89 22.8 11.61C23.04 12.01 25.23 14.45 24.82 13.44C23.89 11.2 21.66 8.2 20.67 5.98C18.31 0.7 18.15 -4.41 15.46 -9.48C14.39 -11.49 12.98 -13.9 11.61 -15.61C9.97 -17.66 4.56 -13.16 2.79 -12.14C1.59 -11.46 0.23 -10.84 -0.65 -9.74C-1.22 -9.03 -6.35 -13.69 -6.78 -14.28C-8 -15.97 -9.82 -17.49 -9.15 -19.84C-8.49 -22.14 -3.95 -24.01 -1.98 -24.67C3.63 -26.54 9.38 -28.67 15.34 -28.67C17.27 -28.67 17.57 -27.42 18.54 -25.88C20.81 -22.28 22.62 -18.39 24.94 -14.81C28.36 -9.52 33.47 -6.81 36.66 -0.95C38.26 1.98 39.86 7.14 39.86 10.78C40.39 14.51 39.21 16.76 38.8 17.71C38.8 18.89 38.86 19.83 38.8 20.9C38.65 23.48 34.4 25.07 33.35 27.3C29.46 35.5 21.93 43.4 13.21 46.25C9.03 47.62 3.96 46.17 -0.12 44.89C-0.92 44.64 -7.32 42.37 -5.98 41.69M-0.65 28.37C-4.57 28.57 -0.22 28.99 1.31 28.72C5.33 28.01 8.06 25.61 11.28 23.33C13.32 21.9 16.35 19.96 17.47 17.71M15.96 36.26C13.31 36.19 10.69 35.3 9.35 32.76C8.85 31.8 8.69 29.26 10.21 29.26C12.05 29.26 13.11 30.12 14.71 31.01C16.2 31.83 15.19 30.41 15.57 29.15C16.48 26.21 17.63 24.79 19.71 27.29C21.72 29.69 17.46 33.94 17.46 36.26M0.95 -10.68C2.63 -9.48 5.77 -7.08 7.67 -9.37C9.29 -11.33 7.92 -13.26 6.73 -14.81M-4.38 -18.01C-2.7 -15.23 -1.07 -12.54 0.95 -10.01M0.95 -20.14C1.98 -17.67 3.14 -15.37 4.68 -13.21M6.28 -21.21C7.48 -19.52 8.57 -17.7 9.48 -15.87M15.34 -12.68C18.3 -14.8 21.26 -16.74 24.4 -18.54M15.34 -7.88C18.46 -10.31 21.82 -12.38 24.94 -14.81M42.53 -19.07C44.69 -22.55 47.38 -25.53 49.99 -28.67M46.26 -6.28C49.82 -8.17 52.87 -10.73 56.39 -12.68M52.12 11.84C57.55 9.71 62.94 7.65 68.65 6.51",
    },
    {
        left: "M-39.35 34.86C-52.46 47.97 -78.78 67.16 -95.95 49.98C-105.7 40.24 -102.95 22.07 -92.84 13.84C-84.04 6.67 -68.51 10.85 -62.8 20.15C-58.74 26.76 -60.81 38.24 -67.73 42.46C-74.12 46.37 -81.92 44.73 -87.34 40.32C-91.69 36.79 -92.97 26.9 -87.38 23.95C-82.76 21.5 -77.25 23.27 -74.65 27.95C-72.81 31.26 -79.42 36.24 -80.83 33.41M-17.3 21.31C-21.33 12.55 -18.44 -3.02 -31.36 -3.88C-36.29 -4.21 -46.94 0.6 -49.69 -5.41C-51.82 -10.04 -47.6 -18.97 -51.94 -22.32C-54.9 -24.61 -58.56 -24.36 -62.06 -24.7C-68.01 -25.27 -71.72 -25.03 -77.19 -28.27M-32.8 57.42C-33.26 59.75 -34.16 63.51 -31.67 65.19C-29.37 66.74 -28.17 61.04 -28.27 59.81C-28.57 56.47 -31.72 58.28 -33.53 58.88M-93.2 79.98C-93.36 79.28 -94.75 74.05 -94.17 77.96C-93.59 81.86 -92.7 85.64 -91.75 89.44M-100.48 85.08C-95.08 84.94 -89.84 84.22 -84.47 83.62",
        right: "M5.36 35.51C15.23 19.59 31.16 7.06 48.83 1.09C58.83 -2.29 71.9 -3.22 80.84 3.59C86.75 8.11 91.12 14.65 92 22.15C92.51 26.54 92.23 34.73 87.41 37.1C82.82 39.37 79.74 35.17 79.64 30.94C79.38 20.36 86.45 9.36 94.62 3.25C104.42 -4.08 118.77 -10.71 131.14 -11.34C134.63 -11.52 138.6 -11.45 142.06 -10.9C143.94 -10.6 145.96 -9.8 147.87 -9.99M31.48 47.17C34.82 47.29 42.44 48.43 43.78 52.13C44.85 55.09 40.34 55.67 39.43 57.82C38.03 61.14 41.78 62.8 44.41 63.03C48.73 63.42 53.01 63 57.3 63.78C64.96 65.16 55.55 70.56 55.52 74.15C55.5 77.45 64.46 77.68 66.53 78.1C72.15 79.24 76.29 81.91 81.3 84.51M129.72 22.43C134.48 25.37 141.61 28.09 145.01 32.61M132.63 36.98C136.39 29.73 140.92 22.93 145.01 15.88",
    },
];

type OwnProps = {
    decorated: boolean;
    children: React.ReactElement<any>;
};

type Props = OwnProps & WithActionSchedulerProps;

type State = {
    annotation: Decoration | null | undefined;
    disappearing: boolean;
};

class DecorationWrapper extends React.Component<Props, State> {
    state: State = {
        annotation: null,
        disappearing: false,
    };

    componentDidMount() {
        if (this.props.decorated) {
            this.addDecoration();
        }
    }

    componentDidUpdate(prevProps: Props, prevState: State) {
        if (!prevProps.decorated && this.props.decorated) {
            this.addDecoration();
        }
        if (prevProps.decorated && !this.props.decorated) {
            this.removeDecoration();
        }
    }

    _disappearingTimer: ITimeout | null | undefined = null;

    addDecoration: () => void = () => {
        // Do not try to load Khannotations outside of a modern browser.
        if (!SVGSVGElement.prototype.getBBox) {
            return;
        }

        this._disappearingTimer?.clear();

        this.setState({
            annotation:
                DECORATION_PATHS[
                    Math.floor(Math.random() * DECORATION_PATHS.length)
                ],
            disappearing: false,
        });
    };

    removeDecoration: () => void = () => {
        if (!this._disappearingTimer?.isSet) {
            this.setState({
                disappearing: true,
            });
            this._disappearingTimer = this.props.schedule.timeout(() => {
                this.setState({
                    annotation: null,
                    disappearing: false,
                });
            }, 300);
        }
    };

    render(): React.ReactNode {
        const {children} = this.props;
        const {annotation, disappearing} = this.state;
        return (
            <AnimationGroup>
                {annotation && (
                    <svg
                        className={css(
                            styles.leftAnnotation,
                            styles.annotation,
                            disappearing && styles.disappearing,
                        )}
                        fill="none"
                        height="51"
                        viewBox="0 0 1 1"
                        width="316"
                        xmlns="http://www.w3.org/2000/svg"
                    >
                        <AnimatedLineDrawing
                            bare={true}
                            d={annotation.left}
                            pathStyle={{
                                stroke: MarketingColors.highlightTeal,
                                strokeLinecap: "round",
                                strokeLinejoin: "round",
                                strokeWidth: "2",
                            }}
                            animation={{
                                animation: "duration",
                                delay: 0,
                                position: "absolute",
                                duration: 300,
                            }}
                            delayRatio={0.2}
                        />
                    </svg>
                )}
                {children}
                {annotation && (
                    <svg
                        className={css(
                            styles.annotation,
                            disappearing && styles.disappearing,
                        )}
                        fill="none"
                        height="51"
                        viewBox="-12 0 1 1"
                        width="1"
                        xmlns="http://www.w3.org/2000/svg"
                    >
                        <AnimatedLineDrawing
                            bare={true}
                            d={annotation.right}
                            pathStyle={{
                                stroke: MarketingColors.highlightTeal,
                                strokeLinecap: "round",
                                strokeLinejoin: "round",
                                strokeWidth: "2",
                            }}
                            animation={{
                                animation: "duration",
                                delay: 300,
                                position: "absolute",
                                duration: 300,
                            }}
                            delayRatio={0.2}
                        />
                    </svg>
                )}
            </AnimationGroup>
        );
    }
}

type ExportProps = WithoutActionScheduler<
    JSX.LibraryManagedAttributes<
        typeof DecorationWrapper,
        React.ComponentProps<typeof DecorationWrapper>
    >
>;
export default withActionScheduler(
    DecorationWrapper,
) as React.ComponentType<ExportProps>;

const styles = StyleSheet.create({
    annotation: {
        pointerEvents: "none",
        overflow: "visible",
        opacity: 1.0,
        transition: "opacity 150ms ease-in-out",
        height: 1,
    },
    leftAnnotation: {
        position: "absolute",
        left: 0,
        width: 1,
    },
    disappearing: {
        opacity: 0.0,
    },
});
